// Manual "sync from WordPress" and "enrich" controls for the Archive page.
//
// The archive normally refreshes from the worker's nightly job at 01:30
// UTC. These buttons are for the rest of the day: an editor publishes
// something in WordPress and wants it in the membership now.
//
// Both jobs run server-side on a background thread, so the POST returns
// straight away and we poll for the outcome. Two runs of the same title
// can't overlap — a title already working (from another admin's click or
// from a scheduled job) reports back instead of starting twice.
//
// Sync and enrich are separate steps on purpose: a synced-but-unenriched
// article is invisible in this page's default view, and enrichment costs
// real money per article, so nothing spends it without a click.

import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'

const SYNC_PUBS: Array<{ slug: string; label: string }> = [
  { slug: 'flight_journal', label: 'Flight Journal' },
  { slug: 'model_airplane_news', label: 'Model Airplane News' },
  { slug: 'rc_car_action', label: 'RC Car Action' },
  { slug: 'diecast', label: 'Die Cast X' },
]

type RunState = 'idle' | 'running' | 'ok' | 'skipped' | 'budget' | 'error'

type SyncSummary = {
  posts_seen: number
  inserted: number
  updated: number
  unchanged: number
  retired: number
  errors: number
  // The ids behind the counts, same bargain as EnrichSummary. Capped at 200
  // server-side: enrichment is limited to 50 per click so its lists are small
  // by construction, but a sync has no ceiling and by-ids refuses over 200.
  // The counts stay uncapped, so a truncated list never makes its own number
  // a lie — SyncOutcome says "first 200" when they disagree.
  inserted_ids: number[]
  updated_ids: number[]
  retired_ids: number[]
}

type EnrichSummary = {
  seen: number
  enriched: number
  failed: number
  skipped_empty: number
  // The ids behind the counts. A count alone is a dead end: 'skipped 2'
  // can't be acted on, two article ids can.
  enriched_ids: number[]
  skipped_empty_ids: number[]
  failed_ids: number[]
}

type Run<S> = {
  state: RunState
  started_at: string | null
  finished_at: string | null
  summary: S | null
  error: string | null
  requested_by: string | null
}

type SyncStatus = {
  running: boolean
  // False once WP_ARCHIVE_SYNC_ENABLED is off: articles are made in AAMCirc.
  enabled?: boolean
  publications: Record<string, Run<SyncSummary>>
}

type EnrichStatus = {
  running: boolean
  publications: Record<string, Run<EnrichSummary> & EnrichBacklog>
  batch_size: number
}

/** Split because they need different actions: 'awaiting' is work a click
 *  does, 'no_text' is work no click can do — video embeds and empty posts
 *  that need unpublishing or fixing at the source. */
type EnrichBacklog = { awaiting: number; no_text: number }

type TriggerResult = { started: string[]; already_running: string[] }

const IDLE_SYNC: Run<SyncSummary> = {
  state: 'idle', started_at: null, finished_at: null,
  summary: null, error: null, requested_by: null,
}

const IDLE_ENRICH: Run<EnrichSummary> & EnrichBacklog = {
  ...IDLE_SYNC, summary: null, awaiting: 0, no_text: 0,
}

// Ops works in Eastern, and the server stamps these in UTC — so pin the
// zone rather than inheriting whatever the viewing browser is set to.
// `timeZoneName` lets Intl label EDT/EST itself; a hardcoded offset
// would be an hour wrong for two-thirds of the year.
const EASTERN = 'America/New_York'

function clockTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN,
    hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  }).format(d)
}

function stamp(run: { finished_at: string | null }): string {
  return run.finished_at ? ` · ${clockTime(run.finished_at)}` : ''
}

/** One line of outcome under each sync button, with the counts clickable.
 *
 *  Same reasoning as EnrichOutcome: "12 new" that can't be opened is a dead
 *  end. Only the counts that HAVE articles behind them become links — "no
 *  changes" and "N errors" stay plain text, the latter because SyncStats
 *  carries error strings with no article ids attached. */
function SyncOutcome({
  run, label, onShowArticles,
}: {
  run: Run<SyncSummary>
  label: string
  onShowArticles: (ids: number[], label: string) => void
}) {
  const cls = `font-mono text-[10px] ${STATE_CLASS[run.state]}`
  const link = 'underline decoration-dotted underline-offset-2 hover:opacity-70'

  if (run.state === 'running') return <span className={cls}>syncing…</span>
  if (run.state === 'skipped') return <span className={cls}>already running</span>
  if (run.state === 'error') {
    return (
      <span className={cls} title={run.error ?? undefined}>
        {run.error ?? 'failed'}
      </span>
    )
  }
  if (run.state !== 'ok') return <span className={cls} />

  const s = run.summary
  if (!s) return <span className={cls}>done</span>

  // What CHANGED, not what was read. The WP delta is inclusive, so every run
  // re-reads the newest post it already has — reporting rows read made an
  // unchanged title claim "1 post synced".
  const bits: ReactNode[] = []

  const countLink = (key: string, n: number, word: string, ids: number[]) => {
    if (n <= 0) return
    if (ids.length === 0) {
      bits.push(<span key={key}>{n} {word}</span>)
      return
    }
    // The server kept at most 200 ids. Say so rather than silently showing a
    // short list under a big number.
    const truncated = ids.length < n
    bits.push(
      <button
        key={key}
        type="button"
        className={link}
        title={truncated ? `Showing the first ${ids.length} of ${n}` : undefined}
        onClick={() => onShowArticles(
          ids,
          `${n} ${word} · ${label}${truncated ? ` (first ${ids.length})` : ''}`,
        )}
      >
        {n} {word}
      </button>,
    )
  }

  countLink('new', s.inserted, 'new', s.inserted_ids)
  countLink('updated', s.updated, 'updated', s.updated_ids)
  countLink('retired', s.retired, 'retired', s.retired_ids)

  if (bits.length === 0) bits.push(<span key="none">no changes</span>)
  if (s.errors > 0) bits.push(<span key="errors">{s.errors} errors</span>)

  return (
    <span className={cls}>
      {bits.map((b, i) => (
        <span key={i}>{i > 0 && ' · '}{b}</span>
      ))}
      {stamp(run)}
    </span>
  )
}

/** One line of outcome under each enrich button. Falls back to the
 *  backlog count so an untouched title still says what's waiting. */
/** One line of outcome under each enrich button, with the counts
 *  clickable.
 *
 *  A count on its own is where this went wrong before: "2 skipped" gave
 *  no way to reach the two articles, so they sat in the backlog
 *  unexplained. Every number here opens the articles behind it. */
function EnrichOutcome({
  run, slug, label, onShowArticles, onShowNeedsEnrichment, onShowNoText,
}: {
  run: Run<EnrichSummary> & EnrichBacklog
  slug: string
  label: string
  onShowArticles: (ids: number[], label: string) => void
  onShowNeedsEnrichment: (slug: string) => void
  onShowNoText: (slug: string) => void
}) {
  const cls = `font-mono text-[10px] ${STATE_CLASS[run.state]}`
  const link = 'underline decoration-dotted underline-offset-2 hover:opacity-70'

  if (run.state === 'running') return <span className={cls}>enriching…</span>
  if (run.state === 'skipped') return <span className={cls}>already running</span>
  if (run.state === 'error') {
    return (
      <span className={cls} title={run.error ?? undefined}>
        {run.error ?? 'failed'}
      </span>
    )
  }

  const s = run.state === 'ok' ? run.summary : null
  const bits: ReactNode[] = []

  if (s) {
    bits.push(
      s.enriched > 0 ? (
        <button
          key="enriched"
          type="button"
          className={link}
          onClick={() => onShowArticles(
            s.enriched_ids, `${s.enriched} enriched · ${label}`,
          )}
        >
          {s.enriched} enriched
        </button>
      ) : <span key="enriched">0 enriched</span>,
    )
    if (s.skipped_empty > 0) {
      bits.push(
        <button
          key="notext"
          type="button"
          className={link}
          title="No extractable text — these never clear on their own"
          onClick={() => onShowArticles(
            s.skipped_empty_ids, `${s.skipped_empty} with no text · ${label}`,
          )}
        >
          {s.skipped_empty} no text
        </button>,
      )
    }
    if (s.failed > 0) {
      bits.push(
        <button
          key="failed"
          type="button"
          className={link}
          onClick={() => onShowArticles(
            s.failed_ids, `${s.failed} failed · ${label}`,
          )}
        >
          {s.failed} failed
        </button>,
      )
    }
  }

  if (run.state === 'budget') bits.push(<span key="budget">batch used up</span>)

  if (run.no_text > 0) {
    bits.push(
      <button
        key="no-text-backlog"
        type="button"
        className={`${link} text-paper-trace`}
        title="Video embeds and empty posts — no click can enrich these"
        onClick={() => onShowNoText(slug)}
      >
        {run.no_text} no text
      </button>,
    )
  }

  if (run.awaiting > 0) {
    bits.push(
      <button
        key="awaiting"
        type="button"
        className={link}
        onClick={() => onShowNeedsEnrichment(slug)}
      >
        {run.awaiting} awaiting
      </button>,
    )
  } else if (!s) {
    bits.push(<span key="done">all enriched</span>)
  }

  return (
    <span className={cls}>
      {bits.map((b, i) => (
        <span key={i}>{i > 0 && ' · '}{b}</span>
      ))}
      {s ? stamp(run) : ''}
    </span>
  )
}

const STATE_CLASS: Record<RunState, string> = {
  idle: 'text-paper-trace',
  running: 'text-amber-glow',
  ok: 'text-radar-green',
  skipped: 'text-paper-trace',
  budget: 'text-amber-glow',
  error: 'text-radar-red',
}

const PRIMARY_BTN =
  'px-3 py-2 bg-amber-glow text-paper-deepest font-mono text-[12px] tracking-label hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed'
const SECONDARY_BTN =
  'px-3 py-2 border border-paper-trace text-paper font-mono text-[12px] tracking-label hover:bg-paper-trace/20 disabled:opacity-40 disabled:cursor-not-allowed'

export function ArchiveSyncBar({
  onShowArticles, onShowNeedsEnrichment, onShowNoText,
}: {
  onShowArticles: (ids: number[], label: string) => void
  onShowNeedsEnrichment: (slug: string) => void
  onShowNoText: (slug: string) => void
}) {
  const qc = useQueryClient()

  const sync = useQuery({
    queryKey: ['lab', 'archive', 'sync'],
    queryFn: () => api.get<SyncStatus>('/api/lab/archive/sync'),
    // Only poll while something is in flight — an idle Archive page
    // shouldn't sit there hitting the API every two seconds.
    refetchInterval: (q) => (q.state.data?.running ? 2000 : false),
  })

  const enrich = useQuery({
    queryKey: ['lab', 'archive', 'enrich'],
    queryFn: () => api.get<EnrichStatus>('/api/lab/archive/enrich'),
    refetchInterval: (q) => (q.state.data?.running ? 2000 : false),
  })

  const runSync = useMutation({
    mutationFn: (slug: string | null) =>
      api.post<TriggerResult>('/api/lab/archive/sync', { publication_slug: slug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lab', 'archive', 'sync'] }),
  })

  const runEnrich = useMutation({
    mutationFn: (slug: string | null) =>
      api.post<TriggerResult>('/api/lab/archive/enrich', { publication_slug: slug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lab', 'archive', 'enrich'] }),
  })

  // When a job finishes, pull its results into the list and stat tiles.
  // Without this the buttons report "+12 synced" over a page still
  // showing yesterday's articles. A sync also changes the enrichment
  // backlog, and enrichment changes what the default (enriched-only)
  // filter will show.
  const syncing = sync.data?.running ?? false
  const enriching = enrich.data?.running ?? false
  const wasBusy = useRef(false)
  const busy = syncing || enriching
  useEffect(() => {
    if (wasBusy.current && !busy) {
      qc.invalidateQueries({ queryKey: ['lab', 'archive', 'list'] })
      qc.invalidateQueries({ queryKey: ['lab', 'archive', 'stats'] })
      qc.invalidateQueries({ queryKey: ['lab', 'archive', 'enrich'] })
    }
    wasBusy.current = busy
  }, [busy, qc])

  const syncRun = (slug: string) => sync.data?.publications[slug] ?? IDLE_SYNC
  const enrichRun = (slug: string) => enrich.data?.publications[slug] ?? IDLE_ENRICH

  const backlog = SYNC_PUBS.reduce((n, p) => n + enrichRun(p.slug).awaiting, 0)
  const batchSize = enrich.data?.batch_size ?? 50

  // "All titles" waits for everything; a single title only waits for
  // itself, so an editor can still sync MAN while Flight Journal runs.
  const syncBusy = syncing || runSync.isPending
  const enrichBusy = enriching || runEnrich.isPending

  return (
    <div className="border-t border-paper-trace mt-4 pt-3 space-y-3">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <div className="label pt-2 pr-1 w-[130px]">Sync from WordPress</div>

        {sync.data?.enabled === false ? (
          <div className="pt-2 font-mono text-[11px] text-paper-dim">
            Off. Articles are made in AAMCirc now, so nothing comes over from WordPress.
          </div>
        ) : <>
        <button
          type="button"
          disabled={syncBusy}
          onClick={() => runSync.mutate(null)}
          className={PRIMARY_BTN}
        >
          ALL TITLES
        </button>

        {SYNC_PUBS.map(({ slug, label }) => {
          const run = syncRun(slug)
          return (
            <div key={slug} className="flex flex-col gap-1">
              <button
                type="button"
                disabled={runSync.isPending || run.state === 'running'}
                onClick={() => runSync.mutate(slug)}
                className={SECONDARY_BTN}
              >
                {label}
              </button>
              <SyncOutcome
                run={run}
                label={label}
                onShowArticles={onShowArticles}
              />
            </div>
          )
        })}
        </>}
      </div>

      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <div className="label pt-2 pr-1 w-[130px]">Awaiting enrichment</div>

        <button
          type="button"
          disabled={enrichBusy || backlog === 0}
          onClick={() => runEnrich.mutate(null)}
          className={PRIMARY_BTN}
        >
          ENRICH NEXT {Math.min(backlog, batchSize)}
        </button>

        {SYNC_PUBS.map(({ slug, label }) => {
          const run = enrichRun(slug)
          return (
            <div key={slug} className="flex flex-col gap-1">
              <button
                type="button"
                disabled={
                  runEnrich.isPending
                  || run.state === 'running'
                  || run.awaiting === 0
                }
                onClick={() => runEnrich.mutate(slug)}
                className={SECONDARY_BTN}
              >
                {label} · {run.awaiting}
              </button>
              <EnrichOutcome
                run={run}
                slug={slug}
                label={label}
                onShowArticles={onShowArticles}
                onShowNeedsEnrichment={onShowNeedsEnrichment}
                onShowNoText={onShowNoText}
              />
            </div>
          )
        })}
      </div>

      <div className="text-paper-trace text-[11px] font-mono space-y-0.5">
        <p>
          Sync pulls posts edited since the last run — automatic every night
          at 01:30 UTC (9:30 PM EDT / 8:30 PM EST the evening before).
        </p>
        <p>
          Enrichment is capped at {batchSize} articles per click, shared
          across titles — each one is an AI call. Click again for the next
          batch. Un-enriched articles are hidden while "Enriched only" is
          ticked.
        </p>
      </div>

      {(runSync.isError || runEnrich.isError) && (
        <p className="text-radar-red text-[11px] font-mono">
          Could not start that run.
        </p>
      )}
    </div>
  )
}
